import type { HouseBodyConfig, RoofConfig, RoofType } from '@/model/schema';
import type { ResolvedConstraints } from './constraints';
import type { StylePresetSpec } from './stylePresets';
import type { Rng } from './rng';

/**
 * Roof selection + parameterization.
 *
 * - Flat roofs suit modern/compact massing.
 * - Gable roofs need a clear ridge axis; we pick the longer footprint axis
 *   so the gable ridge runs parallel to the longer facade (standard).
 * - Hip roofs suit balanced rectangular footprints.
 * - Overhangs stay bounded (preset range).
 * - Height scales with footprint and style.
 */
export function generateRoof(
  rng: Rng,
  body: HouseBodyConfig,
  preset: StylePresetSpec,
  constraints: ResolvedConstraints,
): RoofConfig {
  const type = pickRoofType(rng, preset, constraints);

  const overhang = rng.range(preset.roofOverhang.min, preset.roofOverhang.max);

  // Base roof height scales with the shorter footprint dimension.
  const shortSpan = Math.min(body.footprintWidth, body.footprintDepth);
  const heightByType: Record<RoofType, number> = {
    flat: 0.25, // thin parapet / slab
    gable: rng.range(shortSpan * 0.25, shortSpan * 0.38),
    hip: rng.range(shortSpan * 0.18, shortSpan * 0.3),
  };

  const direction = type === 'gable'
    ? (body.footprintWidth >= body.footprintDepth ? 'x' : 'z')
    : undefined;

  return {
    type,
    overhang,
    height: heightByType[type],
    ...(direction ? { direction } : {}),
  };
}

function pickRoofType(
  _rng: Rng,
  _preset: StylePresetSpec,
  constraints: ResolvedConstraints,
): RoofType {
  // Discrete user choice: always honor, whether the lock is engaged or not.
  // See the note in generateHouse.ts near pickPreset / pickStoryCount.
  return constraints.roofType.value;
}
